/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject, ComponentChildren } from 'preact';

import { DropdownMenu } from './DropdownMenu';
import { SheetMenu } from './SheetMenu';
import { MenuContext } from './MenuContext';
import { getIsMobile } from './menuUtils';

type MenuProps = {
  /**
   * Trigger element reference
   */
  anchorRef: RefObject<HTMLElement>;
  /**
   * Set of menu groups or menu items that menu will hold.Even though you could pass other type of children,
   * is strongly recommended to use MenuItem, menu groups or components that return this type of components
   * since passing other type of components children produces not desirable behaviors.
   */
  children: ComponentChildren;
  /**
   * Specifies if menu is opened.
   */
  isOpen?: boolean;
  /**
   * Property that triggers a callback when menu is supposed to be closed.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   * Specifies some screen reader text. Set it to create an accesible menu.
   */
  'aria-label'?: string;
  /**
   *  Determines focus behavior when the menu is opened.
   */
  initialFocus?: 'menu' | 'firstItem';
};

type CloseDetail =
  | {
      //dismissed represents dismissing the menu using esc,tap and on mobile swiping and taping backdrop
      reason: 'dismissed' | 'itemAction';
    }
  | {
      reason: 'outsideClick';
      target: Element;
    };

// Type only used internally by Sheet and Dropdown menu. If we want to expose both at some point we would need to
// adjust this type.
export type BaseMenuProps = (MainMenu | SubMenu) &
  Omit<MenuProps, 'onClose' | 'initialFocus' | 'isOpen'> & {
    /*None is a valid option here since there are some use cases where a menu is opened and we don't want to focus anything
    eg. When menu is opened due to a hover on submenuitem
    When pasing a number, that is index of the element is going to be focused
    eg. 1 focus the second menuitem*/
    currentFocus?: 'menu' | 'firstItem' | 'secondItem' | 'none';
  };

type MainMenu = {
  menuLevel: 'main';
  menuId?: never;
  closeSubmenu?: never;
};

type SubMenu = {
  menuLevel: 'sub';
  menuId?: string;
  closeSubmenu?: () => void;
};
const isMobile = getIsMobile();

/* Component that is exposed to final user to create a menu. Important to notice that this component is just used by
the final user to express a main menu. It's not reused by us for submenus (we use SheetMenu and DropdownMenu directly there) */

export const Menu = ({ onClose, isOpen, initialFocus, ...menuProps }: MenuProps) => {
  //On close is consumed by the whole menu structure including the main menu and submenu ones.
  return (
    <MenuContext.Provider value={{ onClose, isMobile }}>
      {!isOpen ? null : (
        <>
          {isMobile ? (
            <SheetMenu
              {...{
                ...menuProps,
                currentFocus: initialFocus,
                menuLevel: 'main'
              }}></SheetMenu>
          ) : (
            <DropdownMenu
              {...{
                ...menuProps,
                currentFocus: initialFocus,
                menuLevel: 'main',
                defaultPlacement: 'bottom-start',
                offsetValue: {
                  mainAxis: 4, //TODO: stable does not have a 4px gap, so we'll need to figure out how to make this themable.
                  crossAxis: 0
                },
                flipOptions: {
                  crossAxis: true,
                  mainAxis: true,
                  fallbackPlacements: [
                    'bottom-end',
                    'top-start',
                    'top-end',
                    'end-top',
                    'start-top',
                    'end-bottom',
                    'start-bottom'
                  ]
                }
              }}></DropdownMenu>
          )}
        </>
      )}
    </MenuContext.Provider>
  );
};
